import type { PlanV2IssueDispositionRecord, PlanV2IssueType, PlanV2WorkItemsUrlGroup } from './plan-v2-issues';
import { formatSupportingPagePath, normalizeUrlForCompare } from './topic-supporting-pages';

export interface PlanV2WorkItemDetailV1 {
  v: 1;
  missing?: string[];
  misaligned?: string[];
  /** term → short winner example lines */
  examples?: Record<string, string[]>;
  placement?: string;
  anchor?: string;
  targetPath?: string;
  targetUrl?: string;
  sentence?: string;
  /** @deprecated use gapPages */
  pages?: Array<{ label: string; topics?: string }>;
  gapPages?: Array<{ url: string; missingTopic: string }>;
  /** Selected list tools / section gaps from Supporting pages compose. */
  supportingGaps?: Array<{
    competitorUrl: string;
    kind: 'page' | 'section' | 'list_tool';
    label: string;
    /** H2–H6 when known from compose. */
    headingLevel?: number;
  }>;
  freshness?: { paragraphs: string[] };
  /** Buried strongest-intent section (intent mismatch work items). */
  intentPlacement?: {
    familyBold: string;
    familySuffix: string;
    sectionLabel: string;
    passage: string;
  };
  /**
   * FW-URL-14 answer-delivery structure (separate from vocabulary missing/examples).
   */
  answerStructure?: {
    cue: string;
    family?: string;
    evidence?: Array<{ position: number; spans: string[]; why?: string }>;
  };
  bullets?: string[];
  /** Single on-page technical issue (legacy per-issue work items). */
  technical?: { title: string; details: string[] };
  /** Selected on-page issues from Technical card compose. */
  technicalItems?: Array<{ title: string; details: string[] }>;
  /** Selected internal link suggestions from Issues compose. */
  internalLinkItems?: Array<{
    direction: 'to' | 'from';
    pagePath: string;
    pageUrl: string;
    linkHref: string;
    anchor: string;
    score: number;
    sentence?: string;
    placementParts?: { before: string; anchor: string; after: string } | null;
    /** @deprecated use pagePath + direction */
    targetPath?: string;
    targetUrl?: string;
    sourcePath?: string;
    onThisPage?: boolean;
  }>;
}

const CHIP_BY_TYPE: Record<PlanV2IssueType, string> = {
  serp_phrase: 'Answer structure',
  topic_ownership: 'Supporting pages',
  internal_link: 'Internal link',
  freshness: 'Freshness',
  serp_intent: 'Page type',
  technical_critical: 'Technical issues',
  technical_warnings: 'Technical warnings',
  signals: 'Signals',
  serp_competition: 'SERP competition',
  serp_difficulty: 'SERP difficulty',
  primary_action: 'Priority action',
  url_ignored: 'URL ignored',
  site_wide_technical: 'Site-wide technical',
  section_gap: 'Missing sections',
  // Never reaches a work-items list — a mute is a preference, not work. Present
  // so the map stays total and a new type cannot be added without deciding what
  // it is called.
  check_muted: 'Muted check'
};

export function workItemChipLabel(item: Pick<PlanV2IssueDispositionRecord, 'issueType' | 'cardTitle'>): string {
  return CHIP_BY_TYPE[item.issueType] || item.cardTitle;
}

/**
 * Chip labels for the work-items PDF export (may differ from in-app).
 *
 * `topic_ownership` used to read "Supporting pages & sections" because the card
 * that produced it answered both questions at once. Review now splits them —
 * pages to write and sections to add are different work — so the export says
 * what the item actually is. Items written before the split keep the old
 * wording, which is honest: they really did hold both.
 */
export function workItemPdfChipLabel(item: Pick<PlanV2IssueDispositionRecord, 'issueType' | 'cardTitle'>): string {
  return workItemChipLabel(item);
}

export function parseWorkItemDetail(actionDetail: string | null | undefined): PlanV2WorkItemDetailV1 | null {
  const raw = (actionDetail || '').trim();
  if (!raw.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(raw) as PlanV2WorkItemDetailV1;
    return parsed?.v === 1 ? parsed : null;
  } catch {
    return null;
  }
}

export function workItemDetailFor(item: PlanV2IssueDispositionRecord): PlanV2WorkItemDetailV1 | null {
  return parseWorkItemDetail(item.actionDetail);
}

export function workItemExampleEntries(
  detail: PlanV2WorkItemDetailV1 | null
): Array<{ term: string; lines: string[] }> {
  if (!detail?.examples) return [];
  return Object.entries(detail.examples)
    .filter(([, lines]) => lines?.length)
    .map(([term, lines]) => ({ term, lines }));
}

export function encodeWorkItemDetail(detail: Omit<PlanV2WorkItemDetailV1, 'v'>): string {
  return JSON.stringify({ v: 1, ...detail } satisfies PlanV2WorkItemDetailV1);
}

export const FRESHNESS_INFORMATION_GAIN_NOTE =
  'Only update if you have something genuinely new to add—your own research, a development in the industry, an idea from a customer, and so on. Do not refresh just for the sake of a date change; some topics rarely need updates (for example, “how to tie a tie”), while others change quickly (for example, new AI workflows).';

export const HTTPS_HTTP_LINKS_ISSUE_TITLE = 'HTTPS page contains HTTP links';

/** Short heading for work-item cards (in-app + PDF). */
export const HTTPS_HTTP_LINKS_DISPLAY_TITLE = 'HTTP links';

export const HTTPS_HTTP_LINKS_BODY =
  'HTTPS page contains HTTP links - investigate with Check My Links or view-source:';

/** @deprecated use HTTPS_HTTP_LINKS_BODY */
export const HTTPS_HTTP_LINKS_PDF_TEXT = HTTPS_HTTP_LINKS_BODY;

export const HTTPS_HTTP_LINKS_GUIDANCE =
  'This is a relatively small SEO win—only do it alongside tasks on the same page, unless you have spare time.';

export interface SupportingSectionTreeNode {
  heading: string;
  level: number;
  depth: number;
  isGap: boolean;
  children: SupportingSectionTreeNode[];
}

export interface SupportingGapDisplayBlock {
  kind: 'page' | 'section' | 'list_tool';
  intro: string;
  competitorUrl: string | null;
  items: string[];
  /** Full competitor URLs for page-gap blocks. */
  pageUrls?: string[];
  sectionTree?: SupportingSectionTreeNode[];
}

function parseSectionGapLabelParts(label: string): string[] {
  return label
    .split('>')
    .map((part) => part.trim())
    .filter(Boolean);
}

function sectionHeadingLevelForDepth(depth: number, explicit?: number): number {
  const level = Math.round(Number(explicit) || 0);
  if (level >= 2 && level <= 6) return level;
  return Math.min(6, 2 + depth);
}

/** Merge section gap labels into a tree (shared parents shown once). */
export function buildSectionGapTree(
  gaps: Array<{ label: string; headingLevel?: number }>
): SupportingSectionTreeNode[] {
  const root: SupportingSectionTreeNode[] = [];

  for (const gap of gaps) {
    const parts = parseSectionGapLabelParts(gap.label);
    if (!parts.length) continue;
    let current = root;

    for (let index = 0; index < parts.length; index++) {
      const heading = parts[index];
      const depth = index;
      const isLeaf = index === parts.length - 1;
      const level = isLeaf
        ? sectionHeadingLevelForDepth(depth, gap.headingLevel)
        : sectionHeadingLevelForDepth(depth);

      let node = current.find((entry) => entry.heading === heading);
      if (!node) {
        node = {
          heading,
          level,
          depth,
          isGap: false,
          children: []
        };
        current.push(node);
      } else if (isLeaf && gap.headingLevel) {
        node.level = sectionHeadingLevelForDepth(depth, gap.headingLevel);
      }

      if (isLeaf) node.isGap = true;
      current = node.children;
    }
  }

  return root;
}

export function sectionHeadingLevelChip(level?: number | null): string | null {
  const n = Math.round(Number(level) || 0);
  if (n >= 2 && n <= 6) return `H${n}`;
  return null;
}

export function freshnessParagraphsForDisplay(paragraphs: string[] | undefined | null): string[] {
  const out = [...(paragraphs || [])];
  const suggestsUpdate = out.some((p) => /refresh|updating|fresh information|newer information/i.test(p));
  const hasGuidance = out.some((p) => /genuinely new|just for the sake of a date change/i.test(p));
  if (suggestsUpdate && !hasGuidance) {
    out.push(FRESHNESS_INFORMATION_GAIN_NOTE);
  }
  return out;
}

function isFreshnessInformationGainNote(text: string): boolean {
  const trimmed = text.trim();
  return (
    trimmed === FRESHNESS_INFORMATION_GAIN_NOTE ||
    /genuinely new to add|just for the sake of a date change/i.test(trimmed)
  );
}

const FRESHNESS_DATE_RE =
  /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)[a-z]* \d{1,2}, \d{4}\b/gi;

function freshnessParagraphPdfSegments(text: string): WorkItemPdfSegment[] {
  const segments: WorkItemPdfSegment[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(FRESHNESS_DATE_RE.source, 'gi');
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      segments.push({ text: text.slice(last, match.index), style: 'muted' });
    }
    segments.push({ text: match[0], style: 'bold' });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    segments.push({ text: text.slice(last), style: 'muted' });
  }
  if (!segments.length) {
    segments.push({ text, style: 'muted' });
  }
  return segments;
}

/** Freshness body for the work-items PDF (no information-gain note; dates emphasized). */
export function pushFreshnessPdfLines(
  paragraphs: string[] | undefined | null,
  pushLine: (line: WorkItemPdfLine) => void
): void {
  for (const paragraph of paragraphs || []) {
    const text = paragraph.trim();
    if (!text || isFreshnessInformationGainNote(text)) continue;
    pushLine({ segments: freshnessParagraphPdfSegments(text) });
  }
}

export function pushTechnicalItemsPdfLines(
  items: PlanV2WorkItemDetailV1['technicalItems'],
  legacy: PlanV2WorkItemDetailV1['technical'],
  pushText: (text: string, style?: WorkItemPdfSegmentStyle) => void
): void {
  const list = items?.length ? items : legacy ? [legacy] : [];
  for (const tech of list) {
    if ((tech.title || '').trim() === HTTPS_HTTP_LINKS_ISSUE_TITLE) {
      pushText(HTTPS_HTTP_LINKS_DISPLAY_TITLE, 'bold');
      pushText(HTTPS_HTTP_LINKS_BODY, 'muted');
      continue;
    }
    pushText(tech.title, 'bold');
    const guidance = technicalWorkItemGuidance(tech.title);
    if (guidance) pushText(guidance, 'muted');
    for (const line of tech.details || []) pushText(`• ${line}`, 'muted');
  }
}

export function technicalIssueDisplayTitle(title: string): string {
  if ((title || '').trim() === HTTPS_HTTP_LINKS_ISSUE_TITLE) return HTTPS_HTTP_LINKS_DISPLAY_TITLE;
  return (title || '').trim();
}

export function technicalWorkItemGuidance(title: string): string | null {
  if ((title || '').trim() === HTTPS_HTTP_LINKS_ISSUE_TITLE) {
    return HTTPS_HTTP_LINKS_BODY;
  }
  return null;
}

export interface WorkItemRecapLine {
  key: string;
  count: number;
  /** Text after the count, e.g. "internal link opportunities". */
  text: string;
}

export type WorkItemOverviewLevel = 'High' | 'Medium' | 'Low';

/** @deprecated use WorkItemOverviewLevel */
export type WorkItemOverviewPriority = WorkItemOverviewLevel;

export interface WorkItemOverviewRow {
  key: string;
  type: string;
  count: number;
  impact: WorkItemOverviewLevel;
  effort: WorkItemOverviewLevel;
  description: string;
}

const WORK_ITEM_OVERVIEW_META: Record<
  string,
  { type: string; impact: WorkItemOverviewLevel; effort: WorkItemOverviewLevel; description: string }
> = {
  technical: {
    type: 'Technical issues',
    impact: 'High',
    effort: 'Medium',
    description:
      'Crawl, index, or on-page technical problems that can limit visibility. Fix these before or alongside content work on the same URLs.'
  },
  insecure_links: {
    type: 'Insecure links (HTTPS)',
    impact: 'Low',
    effort: 'Low',
    description:
      'HTTP links on secure pages. A relatively small SEO win—update them when you are already editing the page, unless you have spare time.'
  },
  freshness: {
    type: 'Freshness',
    impact: 'Low',
    effort: 'Medium',
    description:
      'Content may be stale relative to the SERP. Refresh only when you have genuinely new information—not just to change the date.'
  },
  page_updates: {
    type: 'Page updates',
    impact: 'Medium',
    effort: 'High',
    description:
      'Deepen existing pages with missing sections, tools, or information that competitors already cover on similar URLs.'
  },
  supporting_pages: {
    type: 'New supporting pages',
    impact: 'Medium',
    effort: 'High',
    description:
      'Competitors have subtopic pages you lack. Adding supporting pages over time can strengthen topical authority on your main topics.'
  },
  internal_links: {
    type: 'Internal links',
    impact: 'Medium',
    effort: 'Low',
    description:
      'Related pages are not linked together. Contextual internal links help users and search engines discover your best content.'
  },
  intent: {
    type: 'Page type',
    impact: 'Medium',
    effort: 'Low',
    description:
      'SERP page type (how-to, versus, definition, …) does not match your page. Align the page job, or move the matching answer higher.'
  },
  serp_phrases: {
    type: 'Answer structure & entities',
    impact: 'Medium',
    effort: 'Medium',
    description:
      'Two layers: (1) answer-delivery structure winners use in snippets, (2) vocabulary your page still lacks. Match the structure first; close lexical gaps without stuffing.'
  }
};

const WORK_ITEM_OVERVIEW_ORDER = [
  'technical',
  'insecure_links',
  'freshness',
  'page_updates',
  'supporting_pages',
  'internal_links',
  'intent',
  'serp_phrases'
] as const;

/** Overview table rows for the work-items PDF export. */
export function summarizeWorkItemsOverview(items: PlanV2IssueDispositionRecord[]): WorkItemOverviewRow[] {
  const counts = new Map(summarizeWorkItemsRecap(items).map((line) => [line.key, line.count]));
  return WORK_ITEM_OVERVIEW_ORDER.filter((key) => (counts.get(key) || 0) > 0).map((key) => ({
    key,
    count: counts.get(key) || 0,
    ...WORK_ITEM_OVERVIEW_META[key]
  }));
}

function workItemRecapText(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

function countTechnicalIssuesForRecap(
  item: PlanV2IssueDispositionRecord,
  detail: PlanV2WorkItemDetailV1 | null
): { insecure: number; other: number } {
  const titles: string[] = [];
  if (detail?.technicalItems?.length) {
    for (const entry of detail.technicalItems) {
      const title = (entry.title || '').trim();
      if (title) titles.push(title);
    }
  } else if (detail?.technical?.title?.trim()) {
    titles.push(detail.technical.title.trim());
  }

  if (!titles.length && (item.issueType === 'technical_critical' || item.issueType === 'technical_warnings')) {
    const fallback = (item.cardTitle || '').trim();
    if (fallback) titles.push(fallback);
  }

  let insecure = 0;
  let other = 0;
  for (const title of titles) {
    if (title === HTTPS_HTTP_LINKS_ISSUE_TITLE) insecure += 1;
    else other += 1;
  }
  return { insecure, other };
}

export type WorkItemSortCategory =
  | 'technical_other'
  | 'page_updates'
  | 'supporting_pages'
  | 'internal_links'
  | 'intent'
  | 'freshness'
  | 'insecure_links'
  | 'serp_phrases'
  | 'other';

const WORK_ITEM_CATEGORY_SORT_RANK: Record<WorkItemSortCategory, number> = {
  technical_other: 0,
  page_updates: 1,
  supporting_pages: 2,
  internal_links: 3,
  intent: 4,
  freshness: 5,
  insecure_links: 6,
  serp_phrases: 7,
  other: 8
};

/** Classify a work item for display/export ordering. */
export function workItemSortCategory(item: PlanV2IssueDispositionRecord): WorkItemSortCategory {
  const detail = workItemDetailFor(item);

  if (item.issueType === 'technical_critical' || item.issueType === 'technical_warnings') {
    const { insecure, other } = countTechnicalIssuesForRecap(item, detail);
    if (other > 0) return 'technical_other';
    if (insecure > 0) return 'insecure_links';
    return 'technical_other';
  }

  if (item.issueType === 'topic_ownership') {
    const gaps = detail?.supportingGaps || [];
    if (gaps.some((gap) => gap.kind === 'section' || gap.kind === 'list_tool')) return 'page_updates';
    if (gaps.some((gap) => gap.kind === 'page') || (detail?.gapPages?.length ?? 0) > 0) {
      return 'supporting_pages';
    }
    return 'supporting_pages';
  }

  if (item.issueType === 'internal_link') return 'internal_links';
  if (item.issueType === 'serp_intent') return 'intent';
  if (item.issueType === 'freshness') return 'freshness';
  if (item.issueType === 'serp_phrase') return 'serp_phrases';

  return 'other';
}

export function compareWorkItemsForDisplay(
  a: PlanV2IssueDispositionRecord,
  b: PlanV2IssueDispositionRecord
): number {
  const catA = WORK_ITEM_CATEGORY_SORT_RANK[workItemSortCategory(a)];
  const catB = WORK_ITEM_CATEGORY_SORT_RANK[workItemSortCategory(b)];
  if (catA !== catB) return catA - catB;
  const scoreDelta = (b.priorityScore ?? 0) - (a.priorityScore ?? 0);
  if (scoreDelta !== 0) return scoreDelta;
  return a.cardTitle.localeCompare(b.cardTitle);
}

function workItemGroupSortTier(group: PlanV2WorkItemsUrlGroup): number {
  if (group.items.some((item) => workItemSortCategory(item) === 'technical_other')) return 0;
  if (group.items.length > 1) return 1;
  return 2;
}

export function sortWorkItemsWithinGroup(
  items: PlanV2IssueDispositionRecord[]
): PlanV2IssueDispositionRecord[] {
  return [...items].sort(compareWorkItemsForDisplay);
}

/** Sort URL groups and tasks for Work Items tab + PDF export. */
export function sortWorkItemsByUrlGroups(groups: PlanV2WorkItemsUrlGroup[]): PlanV2WorkItemsUrlGroup[] {
  return [...groups]
    .map((group) => ({ ...group, items: sortWorkItemsWithinGroup(group.items) }))
    .sort((a, b) => {
      const tierA = workItemGroupSortTier(a);
      const tierB = workItemGroupSortTier(b);
      if (tierA !== tierB) return tierA - tierB;
      if (b.priorityScore !== a.priorityScore) return b.priorityScore - a.priorityScore;
      return a.url.localeCompare(b.url);
    });
}

/** Roll up accepted work items into human-readable opportunity counts. */
export function summarizeWorkItemsRecap(items: PlanV2IssueDispositionRecord[]): WorkItemRecapLine[] {
  let internalLinks = 0;
  let insecureLinks = 0;
  let newSupportingPages = 0;
  const pageUpdateUrls = new Set<string>();
  const freshnessUrls = new Set<string>();
  let serpPhraseGaps = 0;
  let intentMismatches = 0;
  let otherTechnical = 0;

  const markUrl = (urls: Set<string>, url: string): void => {
    const key = normalizeUrlForCompare(url) || url.trim();
    if (key) urls.add(key);
  };

  const markPageUpdate = (url: string): void => markUrl(pageUpdateUrls, url);
  const markFreshness = (url: string): void => markUrl(freshnessUrls, url);

  for (const item of items) {
    const detail = workItemDetailFor(item);

    switch (item.issueType) {
      case 'internal_link': {
        const count = detail?.internalLinkItems?.length || 0;
        internalLinks += count > 0 ? count : 1;
        break;
      }
      case 'freshness':
        markFreshness(item.url);
        break;
      case 'serp_phrase': {
        const vocab = (detail?.missing?.length || 0) + (detail?.misaligned?.length || 0);
        const structure = detail?.answerStructure?.cue ? 1 : 0;
        const count = vocab + structure;
        serpPhraseGaps += count > 0 ? count : 1;
        break;
      }
      case 'serp_intent':
        intentMismatches += 1;
        break;
      case 'topic_ownership': {
        const gaps = detail?.supportingGaps || [];
        if (gaps.length) {
          for (const gap of gaps) {
            if (gap.kind === 'page') newSupportingPages += 1;
          }
          const needsPageUpdate = gaps.some((gap) => gap.kind === 'section' || gap.kind === 'list_tool');
          if (needsPageUpdate) markPageUpdate(item.url);
        } else if (detail?.gapPages?.length) {
          newSupportingPages += detail.gapPages.length;
        }
        break;
      }
      case 'technical_critical':
      case 'technical_warnings': {
        const { insecure, other } = countTechnicalIssuesForRecap(item, detail);
        insecureLinks += insecure;
        otherTechnical += other;
        break;
      }
      default:
        break;
    }
  }

  const pageUpdates = pageUpdateUrls.size;
  const freshnessUpdates = freshnessUrls.size;

  const lines: WorkItemRecapLine[] = [];
  const push = (key: string, count: number, singular: string, plural: string): void => {
    if (count <= 0) return;
    lines.push({ key, count, text: workItemRecapText(count, singular, plural) });
  };

  push('internal_links', internalLinks, 'internal link opportunity', 'internal link opportunities');
  push('insecure_links', insecureLinks, 'insecure link', 'insecure links');
  push(
    'supporting_pages',
    newSupportingPages,
    'new supporting page opportunity',
    'new supporting page opportunities'
  );
  push('freshness', freshnessUpdates, 'freshness update', 'freshness updates');
  push('page_updates', pageUpdates, 'potential page update', 'potential page updates');
  push('serp_phrases', serpPhraseGaps, 'answer structure gap', 'answer structure gaps');
  push('intent', intentMismatches, 'page type mismatch', 'page type mismatches');
  push('technical', otherTechnical, 'other technical issue', 'other technical issues');

  return lines;
}

export function supportingGapDisplayBlocks(
  gaps: NonNullable<PlanV2WorkItemDetailV1['supportingGaps']>
): SupportingGapDisplayBlock[] {
  const blocks: SupportingGapDisplayBlock[] = [];

  const pages = gaps.filter((gap) => gap.kind === 'page');
  if (pages.length) {
    blocks.push({
      kind: 'page',
      intro:
        "Your competitor has more supporting pages related to your page's main topic, which may boost their authority on the topic. Consider adding new supporting pages, covering subtopics similar to:",
      competitorUrl: null,
      items: [],
      pageUrls: pages.map((gap) => gap.competitorUrl).filter(Boolean)
    });
  }

  const sectionsByUrl = new Map<string, NonNullable<PlanV2WorkItemDetailV1['supportingGaps']>>();
  for (const gap of gaps.filter((g) => g.kind === 'section')) {
    const list = sectionsByUrl.get(gap.competitorUrl) || [];
    list.push(gap);
    sectionsByUrl.set(gap.competitorUrl, list);
  }
  for (const [competitorUrl, sectionGaps] of sectionsByUrl) {
    blocks.push({
      kind: 'section',
      intro:
        "Your competitor has a similar page, but they cover the topic more deeply because they have the following sections you don't have:",
      competitorUrl,
      items: [],
      sectionTree: buildSectionGapTree(sectionGaps)
    });
  }

  const tools = gaps.filter((gap) => gap.kind === 'list_tool');
  if (tools.length) {
    blocks.push({
      kind: 'list_tool',
      intro: 'Your competitor also covers the following tools—consider giving your own take on:',
      competitorUrl: null,
      items: [tools.map((gap) => gap.label).join(', ')]
    });
  }

  return blocks;
}

export type WorkItemPdfSegmentStyle =
  | 'normal'
  | 'bold'
  | 'italic'
  | 'muted'
  | 'link'
  | 'chip'
  | 'headingChip'
  | 'missingHeading';

export interface WorkItemPdfSegment {
  text: string;
  style: WorkItemPdfSegmentStyle;
  url?: string;
}

export interface WorkItemPdfLine {
  segments: WorkItemPdfSegment[];
  /** Left indent in pt (section tree depth). */
  indentPt?: number;
}

export const WORK_ITEM_PDF_LINE_HEIGHT = 11;
export const WORK_ITEM_PDF_PARAGRAPH_GAP = 5;
export const WORK_ITEM_PDF_SECTION_TREE_INDENT = 14;

export const SUPPORTING_SECTION_GAP_PDF_FOOTER =
  'Consider adding your own spin on this as well as any new fresh information about the topic to your existing article.';

function pushSupportingSectionTreePdfLines(
  nodes: SupportingSectionTreeNode[],
  pushLine: (line: WorkItemPdfLine) => void,
  depth = 0
): void {
  for (const node of nodes) {
    const chip = sectionHeadingLevelChip(node.level);
    const segments: WorkItemPdfSegment[] = [];
    if (chip) {
      segments.push({ text: chip, style: 'headingChip' });
      segments.push({ text: ' ', style: 'normal' });
    }
    segments.push({ text: node.heading, style: node.isGap ? 'missingHeading' : 'normal' });
    pushLine({
      segments,
      indentPt: depth * WORK_ITEM_PDF_SECTION_TREE_INDENT
    });
    if (node.children.length) {
      pushSupportingSectionTreePdfLines(node.children, pushLine, depth + 1);
    }
  }
}

export function pushSupportingGapPdfLines(
  gaps: NonNullable<PlanV2WorkItemDetailV1['supportingGaps']>,
  pushText: (text: string, style?: WorkItemPdfSegmentStyle) => void,
  pushLine: (line: WorkItemPdfLine) => void
): void {
  for (const block of supportingGapDisplayBlocks(gaps)) {
    if (block.kind === 'section' && block.competitorUrl) {
      pushLine({
        segments: [
          { text: 'Your competitor has a similar page (', style: 'muted' },
          {
            text: competitorPageLinkLabel(block.competitorUrl),
            style: 'link',
            url: block.competitorUrl
          },
          {
            text: "), but they cover the topic more deeply because they have the following sections you don't have:",
            style: 'muted'
          }
        ]
      });
      if (block.sectionTree?.length) {
        pushSupportingSectionTreePdfLines(block.sectionTree, pushLine);
        pushText(SUPPORTING_SECTION_GAP_PDF_FOOTER, 'muted');
      }
      continue;
    }
    pushText(block.intro, 'muted');
    if (block.kind === 'list_tool') {
      pushText(block.items[0] || '');
    } else if (block.kind === 'page' && block.pageUrls?.length) {
      for (const url of block.pageUrls) {
        pushLine({
          segments: [{ text: competitorPagePath(url), style: 'link', url }]
        });
      }
    } else {
      for (const item of block.items) pushText(`• ${item}`);
    }
  }
}

export function competitorPagePath(url: string): string {
  return formatSupportingPagePath(url) || url;
}

/** Path plus hostname for competitor links (e.g. competitor.com/blog/post). */
export function competitorPageLinkLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./i, '');
    const path = parsed.pathname || '/';
    return `${host}${path}`;
  } catch {
    return competitorPagePath(url);
  }
}

type InternalLinkPdfItem = NonNullable<PlanV2WorkItemDetailV1['internalLinkItems']>[number];

function internalLinkDirectionPdfLabel(direction: 'to' | 'from'): string {
  return direction === 'to' ? 'LINK TO' : 'LINK FROM';
}

/** Rich PDF lines for internal-link work items (matches in-app layout; no relevance score). */
export function pushInternalLinkPdfLines(
  link: InternalLinkPdfItem,
  pushLine: (line: WorkItemPdfLine) => void
): void {
  if (link.direction) {
    const url = (link.pageUrl || link.targetUrl || '').trim();
    const path = (link.pagePath || link.targetPath || url).trim();
    const segments: WorkItemPdfSegment[] = [
      { text: internalLinkDirectionPdfLabel(link.direction), style: 'normal' }
    ];
    if (path) {
      segments.push({ text: ' ', style: 'normal' });
      segments.push({ text: path, style: 'link', url: url || undefined });
    }
    pushLine({ segments });
  } else if (link.anchor) {
    const suffix = link.targetPath ? ` → ${link.targetPath}` : '';
    pushLine({ segments: [{ text: `Link: ${link.anchor}${suffix}`, style: 'normal' }] });
  }

  if (!link.direction && link.sourcePath && link.onThisPage === false) {
    pushLine({ segments: [{ text: `Edit on: ${link.sourcePath}`, style: 'muted' }] });
  }

  if (link.sentence) {
    if (link.placementParts) {
      const href = (link.linkHref || link.targetUrl || '').trim();
      pushLine({
        segments: [
          { text: `\u201c${link.placementParts.before}`, style: 'italic' },
          { text: link.placementParts.anchor, style: 'link', url: href || undefined },
          { text: `${link.placementParts.after}\u201d`, style: 'italic' }
        ]
      });
    } else {
      pushLine({ segments: [{ text: `\u201c${link.sentence}\u201d`, style: 'italic' }] });
    }
  }
}
